import {
  type Fixture,
  type Tournament,
} from '@/types/tournament.model';
import { TVBracket } from './TVBracket';
import { TVGroupCarousel } from './TVGroupCarousel';
import { TV_PALETTE } from './tv-mode.constants';
import { useTVScale } from './TVScaleContext';
import { resolveTvFixturePresentation } from './tv-phase-detection';
import { getEffectiveClassifiedPerGroup } from '@/utils/fixture-utils';

interface Props {
  tournament: Tournament;
  fixture: Fixture | null;
  accent: string;
  accent2: string;
  layout: 'single' | 'dual';
}

const SecHeader = ({
  label,
  title,
  accent,
}: {
  label: string;
  title: string;
  accent: string;
}) => {
  const { s } = useTVScale();
  return (
    <div
      style={{
        paddingBottom: s(12),
        marginBottom: s(12),
        borderBottom: `2px solid ${accent}`,
        flexShrink: 0,
      }}
    >
      <div
        style={{
          fontSize: s(10),
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
          color: accent,
          marginBottom: s(4),
          display: 'flex',
          alignItems: 'center',
          gap: s(6),
        }}
      >
        <span
          className="tv-anim-pulse"
          style={{
            display: 'inline-block',
            width: s(7),
            height: s(7),
            borderRadius: '50%',
            background: accent,
          }}
        />
        {label}
      </div>
      <div
        className="font-gilroy"
        style={{
          fontSize: s(21),
          fontWeight: 900,
          letterSpacing: '0.02em',
          color: TV_PALETTE.navy,
          lineHeight: 1,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {title}
      </div>
    </div>
  );
};

const groupPanel = (
  tournament: Tournament,
  fixture: Fixture,
  accent: string,
) => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      minHeight: 0,
      overflow: 'hidden',
      height: '100%',
    }}
  >
    <SecHeader label="Fase de Grupos" title={titleUpper(tournament)} accent={accent} />
    <div
      style={{
        flex: 1,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}
    >
      <TVGroupCarousel
        groups={fixture.groupList!}
        accent={accent}
        classifiedPerGroup={getEffectiveClassifiedPerGroup(tournament)}
        overflowGroupClassification={tournament.overflowGroupClassification}
        format={tournament.format}
      />
    </div>
  </div>
);

const knockoutPanel = (tournament: Tournament, fixture: Fixture, accent2: string) => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      minHeight: 0,
      overflow: 'hidden',
      height: '100%',
    }}
  >
    <SecHeader
      label="Cuadro Eliminatorio"
      title={titleUpper(tournament)}
      accent={accent2}
    />
    <div
      style={{
        flex: 1,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}
    >
      <TVBracket tree={fixture.knockoutTree} accent={accent2} />
    </div>
  </div>
);

function titleUpper(tournament: Tournament): string {
  return tournament.name?.toUpperCase() ?? 'TORNEO';
}

export const TVTournamentColumn = ({
  tournament,
  fixture,
  accent,
  accent2,
  layout,
}: Props) => {
  const { s } = useTVScale();
  const hasGroups = (fixture?.groupList?.length ?? 0) > 0;
  const hasKnockouts = !!fixture?.knockoutTree?.rootNode;
  const presentation = resolveTvFixturePresentation(fixture);
  const title = titleUpper(tournament);

  const canSplit =
    hasGroups && hasKnockouts && presentation === 'split-groups-knockout';

  if (canSplit) {
    const f = fixture!;
    if (layout === 'single') {
      return (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 7fr) minmax(0, 3fr)',
            gap: s(16),
            height: '100%',
            minHeight: 0,
          }}
        >
          {groupPanel(tournament, f, accent)}
          {knockoutPanel(tournament, f, accent2)}
        </div>
      );
    }
    return (
      <div
        style={{
          display: 'grid',
          gridTemplateRows: 'minmax(0, 7fr) minmax(0, 3fr)',
          gap: s(12),
          height: '100%',
          minHeight: 0,
        }}
      >
        {groupPanel(tournament, f, accent)}
        {knockoutPanel(tournament, f, accent2)}
      </div>
    );
  }

  if (presentation === 'knockout-only' && hasKnockouts) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minHeight: 0,
        }}
      >
        <SecHeader label="Cuadro Eliminatorio" title={title} accent={accent2} />
        <div
          style={{
            flex: 1,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          }}
        >
          <TVBracket tree={fixture!.knockoutTree} accent={accent2} />
        </div>
      </div>
    );
  }

  /* Sin cruces reales en KO: solo grupos; si no hay grupos en el fixture, mensaje de espera */
  if (presentation === 'groups-only') {
    if (hasGroups) {
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            minHeight: 0,
          }}
        >
          <SecHeader label="Fase de Grupos" title={title} accent={accent} />
          <div
            style={{
              flex: 1,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
            }}
          >
            <TVGroupCarousel
              groups={fixture!.groupList!}
              accent={accent}
              classifiedPerGroup={getEffectiveClassifiedPerGroup(tournament)}
              overflowGroupClassification={tournament.overflowGroupClassification}
              format={tournament.format}
            />
          </div>
        </div>
      );
    }
    return (
      <div
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: TV_PALETTE.textMuted,
          fontSize: s(13),
          textAlign: 'center',
          padding: s(20),
        }}
      >
        {hasKnockouts
          ? 'Eliminatoria aún sin partidos confirmados entre equipos.'
          : 'Fixture no disponible todavía'}
      </div>
    );
  }

  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: TV_PALETTE.textMuted,
        fontSize: s(13),
        textAlign: 'center',
        padding: s(20),
      }}
    >
      Fixture no disponible todavía
    </div>
  );
};
